import { SearchInput } from "@/components/molecules/SearchInput";
import { SearchResultsDropdown } from "@/components/organisms/SearchResultsDropdown";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDomains } from "@/hooks/useDomains";
import { useOrgRole } from "@/hooks/useOrgRole";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useSearchbar } from "@/hooks/useSearchbar";
import { FileText } from "lucide-react";

export function SearchBar() {
  const {
    // State
    searchQuery,
    showResults,
    isDisabled,
    filteredResults,
    showAddNewDialog,
    pendingSearchQuery,

    // Actions
    setSearchQuery,
    setShowResults,
    setShowAddNewDialog,
    handleSelectPage,
    handleSelectIdea,
    handleCreateContext,
    handleAddManualPage,

    // Refs
    searchRef,
    inputRef,
  } = useSearchbar();

  const { currentDomain } = useDomains();
  // Viewers search the existing pages/ideas; adding a manual page is a write
  // and `firestore.rules` requires editor for it.
  const { canEdit } = useOrgRole();

  // Handle outside clicks to close dropdown
  useOutsideClick(searchRef, () => setShowResults(false));

  // Handle input focus to show dropdown
  const handleInputFocus = () => {
    if (!isDisabled) setShowResults(true);
  };

  // Handle Enter key for opening Add New dialog
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchQuery.trim() && canEdit) {
      e.preventDefault();
      handleCreateContext();
    }
  };

  return (
    <>
      <div className="relative w-full" ref={searchRef}>
        <SearchInput
          ref={inputRef}
          searchQuery={searchQuery}
          disabled={isDisabled}
          onSearchChange={setSearchQuery}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
        />

        {showResults && currentDomain && (
          <SearchResultsDropdown
            pages={filteredResults.pages}
            ideas={filteredResults.ideas}
            domainId={currentDomain.id}
            searchQuery={searchQuery}
            handleSelectPage={handleSelectPage}
            handleSelectIdea={handleSelectIdea}
            handleCreateContext={canEdit ? handleCreateContext : undefined}
          />
        )}
      </div>

      <Dialog open={showAddNewDialog} onOpenChange={setShowAddNewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add manual page</DialogTitle>
            <DialogDescription>
              Add &ldquo;{pendingSearchQuery}&rdquo; as a page in the current
              workspace?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="justify-start h-auto py-3 px-4"
              onClick={handleAddManualPage}
            >
              <FileText className="mr-3 h-5 w-5 flex-shrink-0" />
              <div className="text-left">
                <div className="font-medium">Add manual page</div>
                <div className="text-xs text-muted-foreground font-normal">
                  Quick-add a page URL to the current workspace
                </div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
